import React, { useEffect, useRef } from 'react';
import type { ChatMessage } from './types';
import { formatMessageTime, formatMessageTimestamp } from './formatMessageTime';

interface ChatTranscriptProps {
  messages: ChatMessage[];
}

// Renders the transcript. Message content and sender names arrive from the agent
// and the contact flow, so they are interpolated as JSX text rather than markup —
// the previous popped-out view built these with innerHTML, which let a display
// name or message body inject HTML into the page.
const ChatTranscript: React.FC<ChatTranscriptProps> = ({ messages }) => {
  const endRef = useRef<HTMLDivElement>(null);

  // Follow the conversation as it grows. Guarded because jsdom (and any
  // non-browser renderer) does not implement scrollIntoView.
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages.length]);

  return (
    <div className="chat-messages" role="log" aria-live="polite" aria-label="Chat transcript">
      {messages.map((message) => (
        <div key={message.id} className={`message ${message.kind === 'system' ? 'system-message' : message.kind}`}>
          {message.kind !== 'system' && (
            <div className="message-sender">
              <span>{message.sender}</span>
              <time className="message-time" dateTime={message.at} title={formatMessageTimestamp(message.at)}>
                {formatMessageTime(message.at)}
              </time>
            </div>
          )}
          <div className="message-content">{message.content}</div>
          {message.kind === 'system' && (
            <time className="message-time" dateTime={message.at} title={formatMessageTimestamp(message.at)}>
              {formatMessageTime(message.at)}
            </time>
          )}
        </div>
      ))}
      <div ref={endRef} />
    </div>
  );
};

export default ChatTranscript;
